/**
 * The vendor intake, from the browser's side.
 *
 * One audience now. This module used to serve two — an applicant on the public
 * form, and an operator working a queue in the rehearsal console — and it held
 * `listApplications`, `releaseApplication` and `dismissApplication` for the
 * second. The console is gone: `onboardVendor` collapsed the four Safe
 * transactions it existed to drive into one, the server files that one on its
 * own, and there is nothing left between a confirmed application and a proposal
 * waiting for signatures. What remains here is what a stranger filling in a form
 * needs.
 *
 * Note what is missing: nothing here ever holds a confirmation token except the
 * one read out of the URL a vendor was mailed. The token is the only credential
 * in this flow, it travels by email and nowhere else, and the submit response
 * deliberately does not contain it.
 *
 * Nothing here reaches a hash either. The address a vendor types is carried to
 * the server and kept in a store of its own; the document whose keccak256 goes
 * on-chain is built from the other three fields and never sees the address. An
 * address in that document would be both published as NFT metadata and would
 * change every `metadataHash` the registry has committed to — see the
 * certificate plan §7.3, which lists "private email" under what an off-chain
 * document may never carry.
 */

export type ApplicationDraft = {
  vendorName: string;
  category: string;
  website: string;
  wallet: string;
  email: string;
  /** Unix seconds, or 0 for a term that does not lapse. */
  expiresAt: number;
};

const BASE = "/api/vendor/applications";

async function parse<T>(response: Response, pick: (body: Record<string, unknown>) => T): Promise<T> {
  const body = (await response.json()) as Record<string, unknown> & { error?: string };
  if (!response.ok) {
    throw new Error(body.error ?? `Server menjawab ${response.status}.`);
  }
  return pick(body);
}

/**
 * Files an application. Returns the id, which is all the applicant ever gets.
 *
 * Not a registration. What comes back means a confirmation letter was accepted
 * by the relay; until the link in it is followed, nothing about this submission
 * reaches the chain or the Safe queue.
 */
export async function submitApplication(draft: ApplicationDraft): Promise<string> {
  const response = await fetch(BASE, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(draft),
  });
  return parse(response, (body) => String(body.id ?? ""));
}

/** Spends the link a vendor was mailed. Returns the company name, for the page. */
export async function confirmApplication(token: string): Promise<string> {
  const response = await fetch(`${BASE}/verify`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ token }),
  });
  return parse(response, (body) => String(body.vendorName ?? ""));
}

// --- what the form checks before it sends anything ---------------------------

/**
 * Deliberately loose — the address is parsed properly by `net/mail` on the
 * server, and this only exists to catch the typo before a round trip. A browser
 * regex that tries to implement RFC 5322 rejects addresses that are perfectly
 * valid, and the field it guards is the one thing standing between a vendor and
 * their certificate.
 */
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function emailProblem(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return "Wajib diisi — sertifikatnya dikirim ke alamat ini.";
  if (trimmed.length > 254) return "Email maksimal 254 karakter.";
  if (!EMAIL_SHAPE.test(trimmed)) return "Harus berbentuk alamat email, contoh: vendor@contoh.com.";
  return null;
}

/** Midnight UTC of a `yyyy-mm-dd` string, as unix seconds. Zero for empty. */
export function expiryToUnix(value: string): bigint {
  if (!value.trim()) return 0n;
  const parsed = Date.parse(`${value}T00:00:00Z`);
  return Number.isNaN(parsed) ? 0n : BigInt(Math.floor(parsed / 1000));
}
